import {
  CSSProperties,
  FC,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useState,
} from "react";
import { useHistory } from "react-router";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { Tooltip, TooltipProps } from "../Tooltip/Tooltip";
import { SortDirection } from "@/utils/api";
import {
  SortActiveDesc,
  SortInactive,
} from "@/components/icons/svg/figma/Sort";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

export type CellProps<RowType> = {
  row: RowType;
  index: number;
};

export type ColumnConfig<RowType, SortKey = undefined> = {
  label: string;
  sortKey?: SortKey;
  align?: CSSProperties["textAlign"];
  width?: CSSProperties["width"];
  tooltipProps?: TooltipProps;
  HeaderCellContent?: FC<{
    config: ColumnConfig<RowType, SortKey>;
  }>;
} & (
  | {
      CellContent: FC<CellProps<RowType>>;
    }
  | {
      getCellText: (row: RowType) => string | number;
    }
);

interface TableProps<RowType, SortKey = undefined> {
  rows: RowType[];
  getRowId: (row: RowType) => string;
  columnConfigs: ColumnConfig<RowType, SortKey>[];
  EmptyState: FC;

  className?: string;
  loading?: boolean;
  rowLinkHref?: (row: RowType) => string | undefined;
  rowLinkTarget?: React.HTMLAttributeAnchorTarget;
  onRowClick?: (row: RowType) => void;
  footerContent?: ReactNode;
  tfootContent?: ReactNode;
  sortBy?: SortKey;
  sortDir?: SortDirection;
  onSortChange?: (sortBy: SortKey, sortDir: SortDirection) => void;
  tracking?: string;
}

export function Table<RowType, SortKey = undefined>({
  className,
  loading,
  columnConfigs,
  EmptyState,
  rows,
  getRowId,
  footerContent,
  tfootContent,
  rowLinkHref,
  rowLinkTarget,
  onRowClick,
  sortBy,
  sortDir,
  onSortChange,
  tracking,
}: TableProps<RowType, SortKey>) {
  const navigate = useHistory().push;
  const handleRowClick = useCallback<MouseEventHandler<HTMLTableRowElement>>(
    (e) => {
      const href = e.currentTarget.dataset.href;
      if (!href) {
        console.warn("No href on table row");
        return;
      }
      if (rowLinkTarget === "_blank") {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(href);
    },
    [navigate, rowLinkTarget],
  );
  const handleColumnClick = useCallback<
    MouseEventHandler<HTMLTableCellElement>
  >(
    (e) => {
      const columnKey = e.currentTarget.dataset.columnKey as
        | SortKey
        | undefined;
      if (!columnKey) {
        console.warn("No column key on the header");
        return;
      }

      if (sortBy === columnKey) {
        const nextSortDir = sortDir === "desc" ? "asc" : "desc";
        onSortChange?.(columnKey, nextSortDir);
      } else {
        onSortChange?.(columnKey, "desc");
      }
    },
    [onSortChange, sortBy, sortDir],
  );

  return (
    <div
      className={twMerge(
        clsx(
          "border-surface-interactive flex max-h-full flex-col overflow-hidden rounded-lg border bg-white shadow dark:bg-gray-100",
          className,
        ),
      )}
    >
      <div className="max-h-full min-h-0 w-full flex-1 overflow-auto">
        <table className="w-full border-spacing-0 text-sm">
          <thead className="outline-dividerBold sticky top-0 left-0 bg-white leading-none outline-1 dark:bg-gray-100">
            <tr>
              {columnConfigs.map((cc) => {
                return (
                  <th
                    key={cc.label}
                    role={cc.sortKey ? "button" : "columnheader"}
                    data-column-key={cc.sortKey}
                    onClick={handleColumnClick}
                    className={cc.sortKey ? "cursor-pointer select-none" : ""}
                    data-track={`${tracking}-${cc.sortKey}`}
                    style={{ width: cc.width }}
                  >
                    <div
                      className={clsx(
                        "group flex items-center p-4 whitespace-nowrap",
                        {
                          "justify-center text-center": cc.align === "center",
                          "justify-start text-left": cc.align === "left",
                          "justify-end text-right": cc.align === "right",
                        },
                      )}
                    >
                      {cc.tooltipProps ? (
                        <Tooltip {...cc.tooltipProps}>
                          <div
                            className={
                              cc.HeaderCellContent
                                ? ""
                                : "underline decoration-dotted"
                            }
                          >
                            {cc.HeaderCellContent ? (
                              <cc.HeaderCellContent config={cc} />
                            ) : (
                              <span>{cc.label}</span>
                            )}
                          </div>
                        </Tooltip>
                      ) : (
                        <div>
                          {cc.HeaderCellContent ? (
                            <cc.HeaderCellContent config={cc} />
                          ) : (
                            <span>{cc.label}</span>
                          )}
                        </div>
                      )}

                      {cc.sortKey && (
                        <div className="text-iconMinimal group-hover:text-iconDefault">
                          {sortBy === cc.sortKey && sortDir === "desc" && (
                            <SortActiveDesc className="size-6" />
                          )}
                          {sortBy === cc.sortKey && sortDir === "asc" && (
                            <SortActiveDesc className="size-6 rotate-180" />
                          )}
                          {sortBy !== cc.sortKey && (
                            <SortInactive className="size-6" />
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-dividerDefault divide-y">
            {!rows.length && !loading && (
              <tr>
                <td colSpan={100}>
                  <EmptyState />
                </td>
              </tr>
            )}
            {!rows.length && loading && (
              <tr>
                <td colSpan={100}>
                  <div className="w-full space-y-2 py-2 text-center">
                    <LoadingSpinner className="size-8" />
                    <div>Loading...</div>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((row, index) => {
              const rowLink = rowLinkHref?.(row);
              const isClickable = rowLink || onRowClick;
              return (
                <tr
                  key={getRowId(row)}
                  data-href={rowLink}
                  role={rowLink ? "link" : "row"}
                  className={clsx(
                    isClickable &&
                      "cursor-pointer transition-colors hover:bg-gray-50",
                  )}
                  onClick={
                    rowLink
                      ? handleRowClick
                      : onRowClick
                        ? () => onRowClick(row)
                        : undefined
                  }
                >
                  {columnConfigs.map((cc) => (
                    <td
                      key={`${cc.label}-${getRowId(row)}`}
                      className={clsx("border-0 p-4", {
                        "text-center": cc.align === "center",
                        "text-left": cc.align === "left",
                        "text-right": cc.align === "right",
                      })}
                    >
                      {"CellContent" in cc ? (
                        <cc.CellContent row={row} index={index} />
                      ) : (
                        cc.getCellText(row)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {tfootContent && (
            <tfoot className="border-dividerDefault border-t bg-gray-50 font-medium">
              {tfootContent}
            </tfoot>
          )}
        </table>
      </div>

      {footerContent && (
        <div className="border-dividerDefault flex-none border-t">
          {footerContent}
        </div>
      )}
    </div>
  );
}

export function useSortByState<ColumnKey>(
  defaultSortBy?: ColumnKey,
  defaultSortDirection?: SortDirection,
) {
  const [sortBy, setSortBy] = useState<ColumnKey | undefined>(defaultSortBy);
  const [sortDir, setSortDir] = useState<SortDirection | undefined>(
    defaultSortDirection,
  );

  const handleSortChange = useCallback(
    (sortBy: ColumnKey, sortDir: SortDirection) => {
      setSortBy(sortBy);
      setSortDir(sortDir);
    },
    [],
  );

  return { sortBy, sortDir, handleSortChange };
}
